import React from "react";
import { Share2, Check } from "lucide-react";
import { Button } from "./Button";

interface ShareCardButtonProps {
  /** Lazily resolves the pre-signed share-card URL (only fetched on click). */
  fetchUrl: () => Promise<{ url: string }>;
  size?: "sm" | "md" | "lg";
  label?: string;
}

/**
 * "Share card" action for shareable trophy / collection-progress cards
 * (PLT-3): native share sheet where available, copy-link fallback
 * otherwise. The URL itself is a pre-signed API link (HTML page with OG
 * meta tags pointing at the rendered PNG) — resolved lazily so unlocking a
 * page full of achievements doesn't fire a request per card.
 */
export function ShareCardButton({ fetchUrl, size = "sm", label = "Share card" }: ShareCardButtonProps) {
  const [state, setState] = React.useState<"idle" | "loading" | "copied" | "error">("idle");

  async function handleClick() {
    if (state === "loading") return;
    setState("loading");
    try {
      const { url } = await fetchUrl();
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ url });
        setState("idle");
        return;
      }
      await navigator.clipboard.writeText(url);
      setState("copied");
      setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2000);
    }
  }

  const text =
    state === "copied" ? "Copied" : state === "error" ? "Couldn't share" : state === "loading" ? "Sharing…" : label;
  const icon = state === "copied" ? <Check size={13} /> : <Share2 size={13} />;

  return (
    <Button variant="secondary" size={size} leftIcon={icon} disabled={state === "loading"} onClick={() => void handleClick()}>
      {text}
    </Button>
  );
}
